"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useLocale } from "next-intl";

// Icons
import {
  MdArrowForward,
  MdOutlineCollectionsBookmark,
  MdOutlineChevronRight,
  MdOutlineChevronLeft,
} from "react-icons/md";

import styles from "./curated-collections.module.css";

const COLLECTIONS_DATA = [
  {
    id: "best-value",
    title: "Cea mai bună valoare",
    subtitle: "Proprietăți cu raport calitate-preț excelent",
    count: "126 proprietăți",
    image: "/placeholder-images/countryside-living.png",
    link: "/collections/best-value",
    hasIcon: true,
  },
  {
    id: "new-projects",
    title: "Proiecte noi",
    subtitle: "Ansambluri rezidențiale și dezvoltări noi",
    count: "84 proiecte",
    image: "/placeholder-images/city-living.png",
    link: "/collections/new-projects",
    hasIcon: false,
  },
  {
    id: "houses-garden",
    title: "Case cu grădină",
    subtitle: "Spațiu și intimitate pentru familia ta",
    count: "156 proprietăți",
    image: "/placeholder-images/coastal-living.png",
    link: "/collections/houses-with-garden",
    hasIcon: false,
  },
  {
    id: "luxury",
    title: "Proprietăți de lux",
    subtitle: "Cele mai exclusiviste reședințe",
    count: "72 proprietăți",
    image: "/placeholder-images/investment.png",
    link: "/collections/luxury",
    hasIcon: true,
  },
  {
    id: "holiday-villas",
    title: "Vile de vacanță",
    subtitle: "La munte, mare sau în natură",
    count: "93 proprietăți",
    image: "/placeholder-images/coastal-living.png",
    link: "/collections/holiday-villas",
    hasIcon: true,
  },
];

export default function CuratedCollections() {
  const locale = useLocale();
  const isRTL = locale === "ar" || locale === "he";

  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  // Dynamic scroll amount (Card width ~280px + gap)
  const scrollAmount = isRTL ? -310 : 310;

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 50);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();

    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;

    if (Math.abs(walk) > 5) {
      setHasDragged(true);
    }
  };

  return (
    <section className={styles.section}>
      {/* Header Area */}
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>Colecții selectate pentru tine</h2>
          <p className={styles.subtitle}>
            Descoperă colecții speciale de proprietăți din România.
          </p>
        </div>
        <Link href="/collections" className={styles.viewAllLink}>
          Vezi toate colecțiile
          <MdArrowForward className={styles.linkIcon} />
        </Link>
      </div>

      {/* Native Drag-to-Scroll Track Area */}
      <div className={styles.sliderWrapper}>
        <div
          className={`${styles.cardsTrack} ${isDragging ? styles.dragging : ""}`}
          ref={scrollRef}
          dir={isRTL ? "rtl" : "ltr"}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          {COLLECTIONS_DATA.map((collection) => (
            <div
              key={collection.id}
              className={styles.cardWrapper}
              onClickCapture={(e) => {
                if (hasDragged) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              }}
            >
              <Link
                href={collection.link}
                className={styles.engineeredCard}
                draggable={false}
              >
                {/* Top Image Container */}
                <div className={styles.imageContainer}>
                  <Image
                    src={collection.image}
                    alt={collection.title}
                    fill
                    sizes="(max-width: 768px) 80vw, 300px"
                    className={styles.cardImage}
                    draggable={false}
                  />
                  {collection.hasIcon && (
                    <div className={styles.iconBadge}>
                      <MdOutlineCollectionsBookmark />
                    </div>
                  )}
                </div>

                {/* Bottom Content Container */}
                <div className={styles.cardContent}>
                  <div className={styles.textStack}>
                    <h3 className={styles.cardTitle}>{collection.title}</h3>
                    <p className={styles.cardSubtitle}>{collection.subtitle}</p>
                  </div>

                  {/* Footer Bar with Action Affordance */}
                  <div className={styles.cardFooter}>
                    <span className={styles.cardCount}>{collection.count}</span>
                    <MdArrowForward className={styles.exploreIcon} />
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Carousel Controls */}
        <div className={styles.carouselControls}>
          <button
            className={styles.controlBtn}
            aria-label="Previous"
            onClick={handleScrollLeft}
          >
            <MdOutlineChevronLeft className={styles.controlIcon} />
          </button>
          <button
            className={styles.controlBtn}
            aria-label="Next"
            onClick={handleScrollRight}
          >
            <MdOutlineChevronRight className={styles.controlIcon} />
          </button>
        </div>
      </div>
    </section>
  );
}
